import { NgClass } from '@angular/common'
import { Component, DestroyRef, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { IntegrationService } from '@app/modules/events/services/integration.service'
import { SelfSettingsService } from '@app/v2/event/services/self-settings.service'
import { EventApp } from '@app/v2/event/types/event-app.type'
import { buildCreateEventAppPayload, HANDSHAKE_SLUG, SNOBALL_SLUG } from '@app/v2/event/utils/event-app.utils'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { TruncationDetectorDirective } from '@app/v2/shared/directives/truncation-detector.directive'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { VirtualService } from '@events/models/virtual-service.interface'
import { EVENT_APPS_LIST } from '@events/repositories/virtual-services.repository'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { finalize } from 'rxjs'

@Component({
  selector: 'app-event-apps-modal',
  standalone: true,
  imports: [DeleteModalComponent, ModalComponent, NgClass, NgxTippyModule, TruncationDetectorDirective],
  templateUrl: './event-apps-modal.component.html',
  styleUrl: './event-apps-modal.component.scss'
})
export class EventAppsModalComponent {
  public readonly eventId = input<number>()
  public readonly eventName = input<string>()
  public readonly eventSlug = input<string>()
  public readonly existingApps = input<EventApp[]>([])
  public readonly appCreated = output<EventApp>()
  public readonly configureRequested = output<EventApp>()

  public readonly plusIcon = `
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_39982_158062)">
    <path d="M15.1111 7.11111H8.88889V0.888889C8.88889 0.653141 8.79524 0.427048 8.62854 0.260349C8.46184 0.0936505 8.23575 0 8 0C7.76425 0 7.53816 0.0936505 7.37146 0.260349C7.20476 0.427048 7.11111 0.653141 7.11111 0.888889V7.11111H0.888889C0.653141 7.11111 0.427048 7.20476 0.260349 7.37146C0.0936505 7.53816 0 7.76425 0 8C0 8.23575 0.0936505 8.46184 0.260349 8.62854C0.427048 8.79524 0.653141 8.88889 0.888889 8.88889H7.11111V15.1111C7.11111 15.3469 7.20476 15.573 7.37146 15.7397C7.53816 15.9064 7.76425 16 8 16C8.23575 16 8.46184 15.9064 8.62854 15.7397C8.79524 15.573 8.88889 15.3469 8.88889 15.1111V8.88889H15.1111C15.3469 8.88889 15.573 8.79524 15.7397 8.62854C15.9064 8.46184 16 8.23575 16 8C16 7.76425 15.9064 7.53816 15.7397 7.37146C15.573 7.20476 15.3469 7.11111 15.1111 7.11111Z" fill="#111928"/>
    </g>
    <defs>
    <clipPath id="clip0_39982_158062">
    <rect width="16" height="16" fill="white"/>
    </clipPath>
    </defs>
    </svg>
  `
  public readonly modalId = 'event-apps-modal'
  public readonly accessRequestModalId = 'event-apps-access-request-confirm-modal'
  public readonly services: VirtualService[] = EVENT_APPS_LIST
  public requestingServiceSlug: string | null = null
  public addingServiceSlug: string | null = null
  public pendingApprovalService: VirtualService | null = null
  public readonly playingServiceId = signal<number | null>(null)

  /**
   * Dynamic confirm copy for the access-request modal based on the pending service.
   * @returns {string}
   */
  public get accessRequestConfirmMessage(): string {
    const name = this.pendingApprovalService?.slackServiceName || this.pendingApprovalService?.title || 'this app'
    return (
      `<strong class="tw-text-gray-500 tw-text-base tw-font-semibold tw-block tw-mb-2">${name} access request</strong>` +
      `<span class="tw-block tw-text-sm tw-font-normal">Thank you for selecting ${name}, your designated account manager will reach out shortly for next steps.</span>`
    )
  }

  private readonly _authService = inject(AuthService)
  private readonly _integrationService = inject(IntegrationService)
  private readonly _selfSettingsService = inject(SelfSettingsService)
  private readonly _alertService = inject(AlertService)
  private readonly _modalService = inject(ModalService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _sanitizer = inject(DomSanitizer)
  private readonly _embedSrcCache = new Map<string, SafeResourceUrl>()

  /**
   * Returns the thumbnail URL for a YouTube video.
   * @param videoId {string} - The ID of the YouTube video.
   * @returns {string} - The thumbnail URL.
   */
  public youtubeThumbnailSrc(videoId: string): string {
    return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
  }

  /**
   * Whether the event-apps hero is a playable video (YouTube or custom video URL).
   * @param service {VirtualService}
   * @returns {boolean}
   */
  public hasVideo(service: VirtualService): boolean {
    return Boolean(service.tutorialVideoId?.trim()) || Boolean(service.videoUrl?.trim())
  }

  /**
   * Hero image for video cards: YouTube thumb, else cover/logo fallback.
   * @param service {VirtualService}
   * @returns {string}
   */
  public heroImageSrc(service: VirtualService): string {
    if (service.tutorialVideoId?.trim()) return this.youtubeThumbnailSrc(service.tutorialVideoId)
    return service.coverImageUrl || service.logo
  }

  /**
   * Tailwind object-fit class for the event-apps hero image. Defaults to contain.
   * @param service {VirtualService}
   * @returns {string}
   */
  public heroObjectFitClass(service: VirtualService): string {
    return service.coverObjectFit === 'cover' ? 'tw-object-cover' : 'tw-object-contain'
  }

  /**
   * Whether the service has a website the hero can open.
   * @param service {VirtualService}
   * @returns {boolean}
   */
  public hasWebsite(service: VirtualService): boolean {
    return Boolean(this._websiteUrl(service))
  }

  /**
   * Plays the event-apps tutorial in the card instead of opening a new tab.
   * @param service {VirtualService}
   * @param event {MouseEvent}
   */
  public onPlayTutorial(service: VirtualService, event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasVideo(service)) return
    this.playingServiceId.set(service.id)
  }

  /**
   * Whether the given service's tutorial is currently playing in its card.
   * @param service {VirtualService}
   * @returns {boolean}
   */
  public isTutorialPlaying(service: VirtualService): boolean {
    return this.playingServiceId() === service.id
  }

  /**
   * Stops any in-card tutorial when the add-apps modal is closed.
   */
  public onModalClosed(): void {
    this.playingServiceId.set(null)
  }

  /**
   * YouTube video id for in-card embed, from `tutorialVideoId` or a YouTube `videoUrl`.
   * @param service {VirtualService}
   * @returns {string | null}
   */
  public youtubeVideoId(service: VirtualService): string | null {
    const fromId = service.tutorialVideoId?.trim()
    if (fromId) return fromId
    return this._youtubeIdFromUrl(service.videoUrl)
  }

  /**
   * Returns a sanitized YouTube embed URL that autoplays in-place with fullscreen enabled.
   * @param videoId {string} - The ID of the YouTube video.
   * @returns {SafeResourceUrl}
   */
  public youtubeEmbedSrc(videoId: string): SafeResourceUrl {
    const cached = this._embedSrcCache.get(videoId)
    if (cached) return cached

    const src = this._sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&fs=1`)
    this._embedSrcCache.set(videoId, src)
    return src
  }

  /**
   * Direct (non-YouTube) video URL for an in-card HTML5 player.
   * @param service {VirtualService}
   * @returns {string | null}
   */
  public directVideoUrl(service: VirtualService): string | null {
    const url = service.videoUrl?.trim()
    if (!url || this.youtubeVideoId(service)) return null
    return url
  }

  /**
   * Opens the service website in a new tab. No-op when there is no website.
   * @param service {VirtualService}
   * @param event {MouseEvent}
   */
  public openWebsite(service: VirtualService, event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()
    const url = this._websiteUrl(service)
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  /**
   * Extracts a YouTube video id from a watch, embed, or youtu.be URL.
   * @param url {string | undefined}
   * @returns {string | null}
   */
  private _youtubeIdFromUrl(url?: string): string | null {
    const raw = url?.trim()
    if (!raw) return null
    const match = raw.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/i)
    return match?.[1] ?? null
  }

  /**
   * Resolves a browsable website URL (adds https when scheme is missing).
   * @param service {VirtualService}
   * @returns {string | null}
   */
  private _websiteUrl(service: VirtualService): string | null {
    const raw = service.website?.trim()
    if (!raw) return null
    if (/^https?:\/\//i.test(raw)) return raw
    return `https://${raw}`
  }

  /**
   * Returns the icon URL for a service.
   * @param service {VirtualService} - The service.
   * @returns {string} - The icon URL.
   */
  public iconSrc(service: VirtualService): string {
    return service.logo
  }

  /**
   * Returns the description text for a service.
   * @param service {VirtualService} - The service.
   * @returns {string} - The description text.
   */
  public descriptionText(service: VirtualService): string {
    return service.description ?? service.informationTemplate
  }

  /**
   * Handles CTA click: Slack for approval-only apps; createEventApp for “Add to my event”.
   * @param service {VirtualService} - The selected service.
   */
  public onServiceAction(service: VirtualService): void {
    if (service.requiresApproval) {
      this._openAccessRequestConfirmModal(service)
      return
    }
    this._onAddEventApp(service)
  }

  /**
   * Confirms the access request from the modal: sends the Slack notification.
   */
  public onAccessRequestConfirmed(): void {
    const service = this.pendingApprovalService
    this.pendingApprovalService = null
    if (!service) return
    this._onRequestAccess(service)
  }

  /**
   * Cancels the access request from the modal: just closes it.
   */
  public onAccessRequestCancelled(): void {
    this.pendingApprovalService = null
    if (this._modalService.isModalOpen(this.accessRequestModalId)) {
      this._modalService.toggleModal(this.accessRequestModalId)
    }
  }

  /**
   * Whether the access-request confirmation modal is currently open.
   * @returns {boolean}
   */
  public isAccessRequestModalOpen(): boolean {
    return this._modalService.isModalOpen(this.accessRequestModalId)
  }

  /**
   * Opens the access-request confirmation modal for an approval-only service.
   * @param service {VirtualService} - The selected service.
   */
  private _openAccessRequestConfirmModal(service: VirtualService): void {
    if (this.requestingServiceSlug) return
    this.pendingApprovalService = service
    if (!this._modalService.isModalOpen(this.accessRequestModalId)) {
      this._modalService.toggleModal(this.accessRequestModalId)
    }
  }

  /**
   * Returns true when request access call is in flight for this service.
   * @param service {VirtualService} - The selected service.
   * @returns {boolean} - Whether request is pending.
   */
  public isRequesting(service: VirtualService): boolean {
    return this.requestingServiceSlug === service.slug
  }

  /**
   * Checks if the add event app call is in flight for the selected service.
   * @param service {VirtualService} - The selected service.
   * @returns {boolean} - Whether the add event app call is pending.
   */
  public isAdding(service: VirtualService): boolean {
    return this.addingServiceSlug === service.slug
  }

  /**
   * Sends an access request to the selected service.
   * @param service {VirtualService} - The selected service.
   * @returns {void}
   */
  private _onRequestAccess(service: VirtualService): void {
    if (this.requestingServiceSlug) return

    const workspace = this._authService.currentWorkspace
    const user = this._authService.user
    if (workspace?.id == null || this.eventId() == null || !user) {
      this._alertService.alert(Alert.ERROR, 'Could not send access request. Missing event or workspace details.')
      return
    }

    let message =
      `New 3rd Party App Request\n` +
      `>• Workspace name: ${workspace.name}\n` +
      `>• Workspace slug: ${workspace.slug}\n` +
      `>• Requested App: *${service.slackServiceName || service.title}*\n` +
      `>• Event name: ${this.eventName()}\n` +
      `>• Event slug: ${this.eventSlug()}\n` +
      `>• Requested by: ${user.full_name}\n` +
      `>• Email: ${user.email}\n`
    if (user.phone_number) message += `>• Phone number: ${user.phone_number}`

    this.requestingServiceSlug = service.slug
    this._integrationService
      .sendSlackMessage({ message })
      .pipe(
        finalize(() => {
          this.requestingServiceSlug = null
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this._modalService.toggleModal(this.modalId)
        },
        error: (err) => {
          this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? 'Failed to send access request')
        }
      })
  }

  /**
   * Checks whether the event already has a Handshake app added.
   * @returns {boolean}
   */
  private _hasHandshakeApp(): boolean {
    return this.existingApps().some((app) => app.location_details?.service?.slug === HANDSHAKE_SLUG)
  }

  /**
   * Checks whether the event already has a Snöball app added.
   * @returns {boolean}
   */
  private _hasSnoballApp(): boolean {
    return this.existingApps().some((app) => app.location_details?.service?.slug === SNOBALL_SLUG)
  }

  /**
   * Creates an event app from the selected service.
   * @param service {VirtualService} - The selected service.
   * @returns {void}
   */
  private _onAddEventApp(service: VirtualService): void {
    if (this.addingServiceSlug) return

    if (service.slug === HANDSHAKE_SLUG && this._hasHandshakeApp()) {
      this._alertService.alert(Alert.WARNING, 'Handshake app is already added to this event.')
      return
    }

    if (service.slug === SNOBALL_SLUG && this._hasSnoballApp()) {
      this._alertService.alert(Alert.WARNING, 'Snöball app is already added to this event.')
      return
    }

    const workspace = this._authService.currentWorkspace
    if (workspace?.id == null || this.eventId() == null) {
      this._alertService.alert(Alert.ERROR, 'Could not add app. Missing event or workspace details.')
      return
    }

    const payload = buildCreateEventAppPayload(this.services, service)
    this.addingServiceSlug = service.slug
    this._selfSettingsService
      .createEventApp(workspace.id, this.eventId(), payload)
      .pipe(
        finalize(() => {
          this.addingServiceSlug = null
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this._modalService.toggleModal(this.modalId)
          this.appCreated.emit(res.data)
          if (service.slug === SNOBALL_SLUG) this.configureRequested.emit(res.data)
          this._alertService.alert(Alert.SUCCESS, res.message)
        },
        error: (err) => this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? 'Failed to add app')
      })
  }
}
